import React, { useState, useEffect } from 'react';
import { openShiftsAPI, visitsAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Broadcast, MapPin, Clock, ChatDots, Sparkle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const CaregiverOpenShifts = () => {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await openShiftsAPI.listForMe();
      setShifts(res.data || []);
    } catch (e) {
      // Silent on dashboard; not critical
      setShifts([]);
    } finally { setLoading(false); }
  };

  const requestShift = async (visitId) => {
    setBusyId(visitId);
    try {
      await visitsAPI.requestShift(visitId, null);
      toast.success('Shift requested — waiting for supervisor approval');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not request shift');
    } finally { setBusyId(null); }
  };

  if (loading || shifts.length === 0) return null;

  return (
    <Card className="rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/5 to-background" data-testid="caregiver-open-shifts-card">
      <CardHeader className="pb-3">
        <CardTitle className="font-heading text-lg flex items-center gap-2">
          <Broadcast weight="duotone" className="w-5 h-5 text-primary" />
          Available Shifts Near You
          <Badge className="ml-1 rounded-full text-xs">{shifts.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {shifts.map(s => {
          const durH = s.duration_minutes ? (s.duration_minutes / 60).toFixed(1) : '?';
          return (
            <div key={s.visit_id} className="rounded-xl border border-border bg-background p-3 space-y-2" data-testid={`available-shift-${s.visit_id}`}>
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div>
                  <p className="font-medium text-sm">{s.client_name}</p>
                  <p className="text-xs text-muted-foreground flex flex-wrap items-center gap-2 mt-0.5">
                    <span className="flex items-center gap-1"><Clock weight="duotone" className="w-3 h-3" />{format(new Date(s.scheduled_date), 'EEE, MMM d')} · {s.scheduled_time} · {durH}h</span>
                    {s.distance_mi != null && (
                      <span className="flex items-center gap-1"><MapPin weight="duotone" className="w-3 h-3" />{s.distance_mi} mi</span>
                    )}
                  </p>
                </div>
                {s.already_requested ? (
                  <Badge className="bg-amber-100 text-amber-800 rounded-full text-xs">Waiting for approval</Badge>
                ) : (
                  <Button
                    size="sm"
                    className="rounded-xl gap-1"
                    onClick={() => requestShift(s.visit_id)}
                    disabled={busyId === s.visit_id}
                    data-testid={`request-shift-btn-${s.visit_id}`}
                  >
                    <Sparkle weight="bold" className="w-3.5 h-3.5" />
                    Request Shift
                  </Button>
                )}
              </div>
              {s.ai_message && (
                <div className="pt-1 mt-1 border-t border-border/50">
                  <p className="text-[10px] uppercase tracking-wider text-primary font-semibold mb-1 flex items-center gap-1"><ChatDots weight="duotone" className="w-3 h-3" />From Dispatch</p>
                  <p className="text-xs">{s.ai_message}</p>
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default CaregiverOpenShifts;
